import { create } from 'zustand'
import {
  fetchMyPrepList,
  getAllMyPrepItems,
  getMyPrepIds,
  putMyPrepOrder,
  type PrepListItem,
} from '../api/prep'
import { resolveServerTotal } from '../utils/prepCounts'
import { sanitizePrepListItem } from '../utils/prepItemNormalization'
import useUserSettingsStore, { type PrepLayout } from './userSettings'

export type DismissUndoSnapshot = {
  itemId: number
  item: PrepListItem
  index: number
  createdAt: number
  wasInMyPrep: boolean
  myPreviousIndex?: number
  myPreviousOrder?: number[]
}

type MyPrepUndoSnapshot = {
  itemId: number
  item: PrepListItem
  previousIndex: number
  previousOrder: number[]
  createdAt: number
}

export type PrepFilterMode = 'single' | 'and' | 'or'

export type PrepListPreviousView = {
  layout: PrepLayout
  selectedItemId?: number | null
  selectedItemIndex?: number
  selectedItemSource?: 'all' | 'my'
  selectedWasOpen?: boolean
  selectedScrollTop?: number
  selectedCategories: string[]
  search: string
  mode: PrepFilterMode
  page: number
  cursor: string | null
}

export interface PrepListState {
  selectedCategories: string[]
  mode: PrepFilterMode
  search: string
  searchOpen: boolean
  page: number
  cursor: string | null
  lineClamp: number
  scrollResetToken: number
  selectedPrepId: number | null
  myOnly: boolean
  myIdsHydrated: boolean
  myIdsLoading: boolean
  myItemsLoading: boolean
  myMembershipVersion: number
  myIds: Set<number>
  myOrderedIds: number[]
  myAllOrderedIds: number[]
  myItems: readonly PrepListItem[]
  myPage: number
  myPerPage: number
  myHasMore: boolean
  myLoadingMore: boolean
  myCount: number
  myCategoryCounts: Record<number, number>
  categoriesById: Record<number, { id: number; slug: string; name: string }>
  categoriesLoadedAt?: number
  myReordering: boolean
  pendingMyUndo: MyPrepUndoSnapshot | null
  previousView?: PrepListPreviousView
  ensureFullMyIds: () => Promise<number[]>
  toggleCategory: (slug: string) => void
  setSelectedCategories: (slugs: string[]) => void
  setMode: (mode: PrepFilterMode) => void
  setSearch: (value: string) => void
  setSearchOpen: (open: boolean) => void
  setPage: (page: number) => void
  setCursor: (cursor: string | null) => void
  setLineClamp: (value: number) => void
  setSelectedPrepId: (id: number | null, opts?: { replace?: boolean }) => void
  clearFiltersKeepMode: () => void
  resetPagination: () => void
  setMyPage: (page: number) => void
  setMyPerPage: (perPage: number) => void
  resetMyPaging: () => void
  enterMyOnly: (selection?: {
    selectedItemId?: number | null
    selectedItemIndex?: number
    selectedItemSource?: 'all' | 'my'
    selectedWasOpen?: boolean
    selectedScrollTop?: number
  }) => Promise<PrepListItem[]>
  exitMyOnly: () => void
  setMyIds: (ids: number[]) => void
  setMyOrderedIds: (ids: number[]) => void
  replaceMyPrepOrder: (ids: number[], total?: number) => void
  loadMoreMyItems: () => Promise<void>
  reorderMyPrep: (fromIndex: number, toIndex: number) => Promise<'success' | 'mismatch' | 'noop'>
  updateMyItem: (item: PrepListItem, inMyPrep: boolean) => void
  syncMyIdsFromItems: (items: Array<{ id: number; is_in_my_prep: boolean }>) => void
  setMyCount: (count: number) => void
  applyMyCategoryCounts: (counts: Record<number, number>) => void
  incMyCount: () => void
  decMyCount: () => void
  incMyCategoryCounts: (item: PrepListItem) => void
  decMyCategoryCounts: (item: PrepListItem) => void
  optimisticAddMyId: (itemId: number) => void
  setPendingMyUndo: (snap: MyPrepUndoSnapshot | null) => void
  applyMyRemovalOptimistic: (
    itemId: number,
  ) => { removedItem?: PrepListItem; previousIndex: number; previousOrder: number[] }
  applyMyUndoRestore: (snap: MyPrepUndoSnapshot) => void
  setCategories: (categories: Array<{ id: number; slug?: string | null; name?: string | null }>) => void
  bumpMyMembershipVersion: () => void
}

const clampLineClamp = (value: number): number => {
  if (Number.isNaN(value) || value <= 0) {
    return 1
  }

  return Math.round(value)
}

const clampMyPerPage = (value: number): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 10
  }

  const normalized = Math.floor(value)

  if (normalized < 3) {
    return 3
  }

  if (normalized > 15) {
    return 15
  }

  return normalized
}

export const usePrepListStore = create<PrepListState>((set, get) => {
  let pendingEnsureMyIds: Promise<number[]> | null = null
  let myIdsEverHydrated = false
  let myIdsHydrationSequence = 0

  const hydrateAllMyIds = async (force = false): Promise<number[]> => {
    if (pendingEnsureMyIds && !force) {
      return pendingEnsureMyIds
    }

    const sequence = ++myIdsHydrationSequence
    const fetchPromise = (async () => {
      const { items: fullIds, total } = await getMyPrepIds()
      const normalized = normalizeIdList(fullIds)
      const resolvedTotal =
        typeof total === 'number' && Number.isFinite(total)
          ? Math.max(0, Math.floor(total))
          : normalized.length

      if (sequence !== myIdsHydrationSequence) {
        return get().myAllOrderedIds
      }

      let canonical = normalized

      set((current) => {
        const partial = reconcileAfterOrderChange(current, normalized, {
          nextCount: resolvedTotal,
        })

        canonical = partial.myAllOrderedIds

        return {
          ...current,
          ...partial,
          myIdsHydrated: true,
        }
      })

      myIdsEverHydrated = true

      return canonical
    })()

    const pending = fetchPromise.then(
      (ids) => {
        if (pendingEnsureMyIds === pending) {
          pendingEnsureMyIds = null
        }
        return ids
      },
      (error) => {
        if (pendingEnsureMyIds === pending) {
          pendingEnsureMyIds = null
        }
        throw error
      },
    )
    pendingEnsureMyIds = pending

    return pending
  }

  const hydrateAllMyItems = async (): Promise<{ items: PrepListItem[]; total: number }> => {
    const { items, total } = await getAllMyPrepItems()
    const { items: normalizedItems, orderedIds } = normalizeMyPrepItems(items)
    const resolvedTotal =
      typeof total === 'number' && Number.isFinite(total)
        ? Math.max(0, Math.floor(total))
        : orderedIds.length

    set((state) => {
      const nextPerPage =
        orderedIds.length > 0 ? Math.max(state.myPerPage, orderedIds.length) : state.myPerPage
      const normalizedPerPage = clampMyPerPage(nextPerPage)
      const requiredPages =
        orderedIds.length > 0 && normalizedPerPage > 0
          ? Math.max(1, Math.ceil(orderedIds.length / normalizedPerPage))
          : 1

      return {
        ...state,
        myItems: normalizedItems,
        myOrderedIds: orderedIds,
        myAllOrderedIds: orderedIds,
        myIds: new Set(orderedIds),
        myCount: resolvedTotal,
        myHasMore: false,
        myLoadingMore: false,
        myIdsHydrated: true,
        myPage: requiredPages,
        myPerPage: nextPerPage,
      }
    })

    return { items: normalizedItems, total: resolvedTotal }
  }

  const ensureFullMyIds = async (): Promise<number[]> => {
    const state = get()

    if (state.myIdsHydrated) {
      myIdsEverHydrated = true
      return state.myAllOrderedIds
    }

    if (myIdsEverHydrated) {
      return state.myAllOrderedIds
    }

    if (pendingEnsureMyIds) {
      try {
        return await pendingEnsureMyIds
      } catch (error) {
        return get().myAllOrderedIds
      }
    }

    try {
      return await hydrateAllMyIds()
    } catch (error) {
      return get().myAllOrderedIds
    }
  }

  const moveWithinArray = (source: readonly number[], fromIndex: number, toIndex: number): number[] | null => {
    if (!Array.isArray(source) || source.length === 0) {
      return null
    }

    const maxIndex = source.length - 1
    const clampedFrom = Number.isFinite(fromIndex)
      ? Math.max(0, Math.min(maxIndex, Math.floor(fromIndex)))
      : 0
    const boundedTo = Number.isFinite(toIndex)
      ? Math.max(0, Math.min(source.length, Math.floor(toIndex)))
      : clampedFrom

    const next = source.slice()
    const [moved] = next.splice(clampedFrom, 1)
    if (moved === undefined) {
      return null
    }

    const insertIndex = Math.max(0, Math.min(next.length, boundedTo))

    if (clampedFrom === insertIndex) {
      return [...source]
    }

    next.splice(insertIndex, 0, moved)
    return next
  }

  return {
    selectedCategories: [],
    mode: 'or',
    search: '',
    searchOpen: false,
    page: 1,
    cursor: null,
    lineClamp: 3,
    scrollResetToken: 0,
    selectedPrepId: null,
    myOnly: false,
    myIdsHydrated: false,
    myIdsLoading: false,
    myItemsLoading: false,
    myMembershipVersion: 0,
    myIds: new Set<number>(),
    myOrderedIds: [],
    myAllOrderedIds: [],
    myItems: [] as readonly PrepListItem[],
    myPage: 1,
    myPerPage: 10,
    myHasMore: true,
    myLoadingMore: false,
    myCount: 0,
    myCategoryCounts: {},
    categoriesById: {},
    categoriesLoadedAt: undefined,
    myReordering: false,
    pendingMyUndo: null,
    previousView: undefined,
    ensureFullMyIds,
    bumpMyMembershipVersion: () =>
      set((state) => ({
        ...state,
        myMembershipVersion: state.myMembershipVersion + 1,
      })),
    toggleCategory: (slug) =>
      set((state) => {
        const normalized = slug.trim()

        if (normalized === '') {
          return state
        }

        if (state.mode === 'single') {
          if (state.selectedCategories.length === 1 && state.selectedCategories[0] === normalized) {
            return {
              ...state,
              selectedCategories: [],
              page: 1,
              cursor: null,
            }
          }

          return {
            ...state,
            selectedCategories: [normalized],
            page: 1,
            cursor: null,
          }
        }

        const exists = state.selectedCategories.includes(normalized)
        const selectedCategories = exists
          ? state.selectedCategories.filter((value) => value !== normalized)
          : [...state.selectedCategories, normalized]

        return {
          ...state,
          selectedCategories,
          page: 1,
          cursor: null,
        }
      }),
    setSelectedCategories: (slugs) =>
      set((state) => {
        const seen = new Set<string>()
        const normalized: string[] = []

        for (const value of Array.isArray(slugs) ? slugs : []) {
          const trimmed = value.trim()
          if (!trimmed || seen.has(trimmed)) {
            continue
          }
          seen.add(trimmed)
          normalized.push(trimmed)
        }

        if (
          normalized.length === state.selectedCategories.length &&
          normalized.every((value, index) => value === state.selectedCategories[index])
        ) {
          return state
        }

        return {
          ...state,
          selectedCategories: normalized,
          page: 1,
          cursor: null,
        }
      }),
  setMode: (mode) => {
    const normalizedMode: PrepFilterMode = mode === 'and' ? 'and' : mode === 'single' ? 'single' : 'or'
    const previousMode = get().mode

    if (previousMode === normalizedMode) {
      return
    }

    set((state) => ({
      ...state,
      mode: normalizedMode,
      page: 1,
      cursor: null,
    }))
  },
  setSearch: (value) =>
    set((state) => ({
      ...state,
      search: value.trimStart(),
      page: 1,
      cursor: null,
    })),
  setSearchOpen: (open) =>
    set((state) => ({
      ...state,
      searchOpen: Boolean(open),
    })),
  setPage: (page) =>
    set((state) => ({
      ...state,
      page: Number.isFinite(page) && page > 0 ? Math.floor(page) : 1,
    })),
  setCursor: (cursor) =>
    set((state) => ({
      ...state,
      cursor: cursor ?? null,
    })),
  setSelectedPrepId: (id, opts) =>
    set((state) => {
      const normalized = id ?? null
      if (state.selectedPrepId === normalized) {
        return state
      }

      if (opts?.replace) {
        return {
          ...state,
          selectedPrepId: normalized,
        }
      }

      return {
        ...state,
        selectedPrepId: normalized,
      }
    }),
  setLineClamp: (value) =>
    set((state) => ({
      ...state,
      lineClamp: clampLineClamp(value),
    })),
  clearFiltersKeepMode: () => {
    const { myOnly, resetMyPaging } = get()
    if (myOnly) {
      resetMyPaging()
    }
    set((state) => ({
      ...state,
      selectedCategories: [],
      search: '',
      searchOpen: false,
      page: 1,
      cursor: null,
      scrollResetToken: state.scrollResetToken + 1,
    }))
  },
  resetPagination: () =>
    set((state) => ({
      ...state,
      page: 1,
      cursor: null,
      scrollResetToken: state.scrollResetToken + 1,
    })),
  setMyPage: (page) =>
    set((state) => {
      const normalized = Number.isFinite(page) && page > 0 ? Math.floor(page) : 1
      if (state.myPage === normalized) {
        return state
      }
      return {
        ...state,
        myPage: normalized,
      }
    }),
  setMyPerPage: (perPage) =>
    set((state) => {
      const normalized = clampMyPerPage(perPage)
      if (state.myPerPage === normalized) {
        return state
      }
      return {
        ...state,
        myPerPage: normalized,
      }
    }),
  resetMyPaging: () =>
    set((state) => ({
      ...state,
      myPage: 1,
      myHasMore: true,
      myLoadingMore: false,
    })),
  enterMyOnly: async (selection) => {
    const currentState = get()

    if (!currentState.myOnly) {
      const userSettingsState = useUserSettingsStore.getState()
      const layout = userSettingsState.prep.layout

      set((state) => ({
        ...state,
        previousView:
          state.previousView ?? {
            layout,
            selectedCategories: [...state.selectedCategories],
            search: state.search,
            mode: state.mode,
            page: state.page,
            cursor: state.cursor,
            selectedItemId: selection?.selectedItemId ?? null,
            selectedItemIndex:
              typeof selection?.selectedItemIndex === 'number' &&
              Number.isFinite(selection.selectedItemIndex) &&
              selection.selectedItemIndex >= 0
                ? selection.selectedItemIndex
                : undefined,
            selectedItemSource: selection?.selectedItemSource,
            selectedWasOpen: selection?.selectedWasOpen,
            selectedScrollTop: selection?.selectedScrollTop,
          },
        page: 1,
        cursor: null,
        scrollResetToken: state.scrollResetToken + 1,
      }))

      if (layout !== 'single') {
        userSettingsState.setPrepLayout('single')
      }
    }

    myIdsEverHydrated = false

    set((state) => ({
      ...state,
      myOnly: true,
      myItems: [],
      myOrderedIds: [],
      myAllOrderedIds: [],
      myIds: new Set<number>(),
      myIdsHydrated: false,
      myIdsLoading: true,
      myItemsLoading: true,
    }))

    try {
      const { items } = await hydrateAllMyItems()
      await hydrateAllMyIds()
      set((state) => ({ ...state, myOnly: true, myIdsLoading: false, myItemsLoading: false }))
      return items
    } catch (error) {
      set((state) => ({ ...state, myIdsLoading: false, myItemsLoading: false }))
      throw error
    }
  },
  exitMyOnly: () => {
    const snapshot = get().previousView
    if (!get().myOnly) {
      return
    }

    if (snapshot) {
      useUserSettingsStore.getState().setPrepLayout(snapshot.layout)
      set((state) => ({
        ...state,
        myOnly: false,
        myItemsLoading: false,
        previousView: undefined,
        selectedCategories: [...snapshot.selectedCategories],
        search: snapshot.search,
        mode: snapshot.mode,
        page: snapshot.page,
        cursor: snapshot.cursor,
        scrollResetToken: state.scrollResetToken + 1,
      }))
      return
    }

    set((state) => ({
      ...state,
      myOnly: false,
      myItemsLoading: false,
      scrollResetToken: state.scrollResetToken + 1,
    }))
  },
  setMyIds: (ids) =>
    set((state) => ({
      ...state,
      myIds: new Set(ids.filter((id) => Number.isFinite(id))),
    })),
  setMyOrderedIds: (ids) =>
    set((state) => {
      const normalized = normalizeIdList(ids)
      const itemMap = new Map(state.myItems.map((entry) => [entry.id, entry]))
      const nextAll = applySubsetOrdering(state.myAllOrderedIds, normalized)
      const loadedOrder = normalized.length > 0
        ? normalized.filter((id) => itemMap.has(id))
        : buildLoadedOrder(nextAll, itemMap, state.myPage, state.myPerPage)
      const orderedItems = rebuildItemsFromOrder(loadedOrder, itemMap)
      return {
        ...state,
        myOrderedIds: loadedOrder,
        myAllOrderedIds: nextAll,
        myIds: new Set(nextAll),
        myItems: orderedItems,
      }
    }),
  replaceMyPrepOrder: (ids, total) => {
    myIdsHydrationSequence += 1
    pendingEnsureMyIds = null
    const normalized = normalizeIdList(ids)
    set((state) => ({
      ...state,
      ...reconcileAfterOrderChange(state, normalized, {
        nextCount:
          typeof total === 'number' && Number.isFinite(total)
            ? Math.max(0, Math.floor(total))
            : normalized.length,
      }),
      myIdsHydrated: true,
      myMembershipVersion: state.myMembershipVersion + 1,
    }))
    myIdsEverHydrated = true
  },
  loadMoreMyItems: async () => {
    const { myOnly, myLoadingMore, myHasMore, myPage, myPerPage } = get()
    if (myOnly) {
      return
    }

    if (myLoadingMore || !myHasMore) {
      return
    }

    const targetPage = myPage + 1
    const effectivePerPage = clampMyPerPage(myPerPage)

    set((state) => ({
      ...state,
      myLoadingMore: true,
      myPerPage: clampMyPerPage(state.myPerPage),
    }))

    try {
      const response = await fetchMyPrepList({ page: targetPage, limit: effectivePerPage })
      const { items } = normalizeMyPrepItems(response.items)

      const normalizedPage =
        typeof response.page === 'number' && Number.isFinite(response.page)
          ? Math.max(1, Math.floor(response.page))
          : targetPage
      const normalizedPerPage =
        typeof response.per_page === 'number' && Number.isFinite(response.per_page)
          ? clampMyPerPage(response.per_page)
          : effectivePerPage
      const normalizedTotal =
        typeof response.total === 'number' && Number.isFinite(response.total)
          ? Math.max(0, Math.floor(response.total))
          : resolveServerTotal(response, items)

      set((state) => {
        const itemMap = new Map(state.myItems.map((entry) => [entry.id, entry]))
        const existingAll = new Set(state.myAllOrderedIds)
        const nextAll = [...state.myAllOrderedIds]

        for (const item of items) {
          const mergedItem = { ...item, is_in_my_prep: true }
          itemMap.set(item.id, mergedItem)
          if (!existingAll.has(item.id)) {
            existingAll.add(item.id)
            nextAll.push(item.id)
          }
        }

        const loadedOrder = buildLoadedOrder(nextAll, itemMap, normalizedPage, normalizedPerPage)
        const mergedItems = rebuildItemsFromOrder(loadedOrder, itemMap)
        const nextHasMore = normalizedPage * normalizedPerPage < normalizedTotal

        return {
          ...state,
          myItems: mergedItems,
          myOrderedIds: loadedOrder,
          myAllOrderedIds: nextAll,
          myIds: new Set(nextAll),
          myPage: normalizedPage,
          myPerPage: normalizedPerPage,
          myHasMore: nextHasMore,
          myCount: normalizedTotal,
          myIdsHydrated: true,
        }
      })
    } catch (error) {
      throw error
    } finally {
      set((state) => ({ ...state, myLoadingMore: false }))
    }
  },
  reorderMyPrep: async (fromIndex, toIndex) => {
    if (!Number.isFinite(fromIndex) || !Number.isFinite(toIndex)) {
      return 'noop'
    }

    if (get().myReordering) {
      return 'noop'
    }

    let baseOrder = get().myAllOrderedIds

    if (!Array.isArray(baseOrder) || baseOrder.length === 0) {
      baseOrder = await ensureFullMyIds()
    }

    if (!Array.isArray(baseOrder) || baseOrder.length === 0) {
      return 'noop'
    }

    const maxIndex = baseOrder.length - 1
    const normalizedFrom = Math.max(0, Math.min(maxIndex, Math.floor(fromIndex)))
    const normalizedTo = Math.max(0, Math.min(baseOrder.length, Math.floor(toIndex)))

    const draggedId = baseOrder[normalizedFrom]
    if (draggedId === undefined) {
      return 'noop'
    }

    const working = baseOrder.slice()
    working.splice(normalizedFrom, 1)
    const insertIndex = Math.max(0, Math.min(working.length, normalizedTo))
    working.splice(insertIndex, 0, draggedId)

    if (working.length === baseOrder.length && working.every((value, index) => value === baseOrder[index])) {
      return 'noop'
    }

    const rollback = baseOrder.slice()

    set((state) => ({
      ...state,
      myReordering: true,
      ...reconcileAfterOrderChange(state, working),
    }))

    try {
      const response = await putMyPrepOrder(working)
      const responseOrder = Array.isArray(response?.ordered_ids)
        ? normalizeIdList(response.ordered_ids)
        : null
      const nextOrder = responseOrder ?? normalizeIdList(working)

      set((state) => ({
        ...state,
        myReordering: false,
        ...reconcileAfterOrderChange(state, nextOrder),
      }))

      return 'success'
    } catch (error) {
      const status = (error as { status?: number; response?: { status?: number } })?.status ??
        (error as { status?: number; response?: { status?: number } })?.response?.status
      const data = (error as { data?: unknown })?.data
      const code =
        data && typeof data === 'object' && 'code' in data
          ? String((data as { code?: unknown }).code ?? '')
          : undefined

      if (status === 400 && code === 'rpa_my_prep_order_mismatch') {
        set((state) => ({
          ...state,
          myReordering: false,
          ...reconcileAfterOrderChange(state, rollback),
        }))

        try {
          await hydrateAllMyIds()
        } catch {
          // Swallow hydrate errors; the optimistic rollback already restored the previous order.
        }

        return 'mismatch'
      }

      set((state) => ({
        ...state,
        myReordering: false,
        ...reconcileAfterOrderChange(state, rollback),
      }))

      throw error
    }
  },
  updateMyItem: (item, inMyPrep) => {
    let membershipChanged = false
    set((state) => {
      const sanitized = sanitizePrepListItem(item, { inMyPrep })

      if (!sanitized) {
        return state
      }

      const id = sanitized.id
      const nextIds = new Set(state.myIds)
      const itemMap = new Map(state.myItems.map((entry) => [entry.id, entry]))

      const hadId = state.myIds.has(id)

      if (inMyPrep) {
        nextIds.add(id)
      } else {
        nextIds.delete(id)
      }

      if (inMyPrep) {
        const existing = itemMap.get(id)
        let merged: PrepListItem = sanitized

        if (existing) {
          const shouldMerge =
            existing.headline !== sanitized.headline ||
            existing.tease !== sanitized.tease ||
            existing.preview !== sanitized.preview ||
            existing.published_at !== sanitized.published_at ||
            existing.is_in_my_prep !== sanitized.is_in_my_prep ||
            existing.short_story_present !== sanitized.short_story_present ||
            existing.short_story_preview !== sanitized.short_story_preview ||
            existing.short_story_preferred !== sanitized.short_story_preferred ||
            existing.short_story_neutral !== sanitized.short_story_neutral ||
            existing.categories !== sanitized.categories ||
            existing.primary_category !== sanitized.primary_category ||
            existing.primary_category_id !== sanitized.primary_category_id ||
            !areImagesEqual(existing.image ?? null, sanitized.image ?? null)

          merged = shouldMerge
            ? {
                ...existing,
                ...sanitized,
                categories: sanitized.categories,
                primary_category: sanitized.primary_category,
                primary_category_id: sanitized.primary_category_id,
                image: sanitized.image ?? null,
              }
            : existing
        }

        itemMap.set(id, merged)
      } else {
        itemMap.delete(id)
      }

      const nextAll = inMyPrep
        ? [...state.myAllOrderedIds.filter((value) => value !== id), id]
        : state.myAllOrderedIds.filter((value) => value !== id)

      const partial = reconcileAfterOrderChange(state, nextAll, {
        itemMapOverride: itemMap,
      })

      if (
        partial.myAllOrderedIds === state.myAllOrderedIds &&
        partial.myOrderedIds === state.myOrderedIds &&
        partial.myItems === state.myItems &&
        nextIds.size === state.myIds.size &&
        Array.from(nextIds).every((value) => state.myIds.has(value))
      ) {
        return state
      }

      membershipChanged = true
      return {
        ...state,
        ...partial,
        myIds: nextIds,
        myCount: Math.max(0, partial.myAllOrderedIds.length),
      }
    })
    if (membershipChanged) {
      myIdsHydrationSequence += 1
      pendingEnsureMyIds = null
      get().bumpMyMembershipVersion()
    }
  },
  syncMyIdsFromItems: (items) =>
    set((state) => {
      if (!items || items.length === 0) {
        return state
      }
      const next = new Set(state.myIds)
      let changed = false

      for (const item of items) {
        if (item.is_in_my_prep) {
          if (!next.has(item.id)) {
            next.add(item.id)
            changed = true
          }
        } else if (next.has(item.id)) {
          next.delete(item.id)
          changed = true
        }
      }

      if (!changed) {
        return state
      }

      return { ...state, myIds: next }
    }),
  setMyCount: (count) =>
    set((state) => ({
      ...state,
      myCount: Number.isFinite(count) && count >= 0 ? Math.floor(count) : 0,
    })),
  applyMyCategoryCounts: (counts) =>
    set((state) => ({
      ...state,
      myCategoryCounts: Object.fromEntries(
        Object.entries(counts ?? {}).map(([key, value]) => [Number(key), Math.max(0, Number(value) || 0)]),
      ),
    })),
  incMyCount: () =>
    set((state) => ({
      ...state,
      myCount: state.myCount + 1,
    })),
  decMyCount: () =>
    set((state) => ({
      ...state,
      myCount: state.myCount > 0 ? state.myCount - 1 : 0,
    })),
  incMyCategoryCounts: (item) =>
    set((state) => {
      const ids = collectCategoryIds(item)
      if (ids.length === 0) {
        return state
      }
      const next = { ...state.myCategoryCounts }
      let changed = false
      for (const id of ids) {
        const current = next[id] ?? 0
        next[id] = current + 1
        changed = true
      }
      if (!changed) {
        return state
      }
      return { ...state, myCategoryCounts: next }
    }),
  decMyCategoryCounts: (item) =>
    set((state) => {
      const ids = collectCategoryIds(item)
      if (ids.length === 0) {
        return state
      }
      const next = { ...state.myCategoryCounts }
      let changed = false
      for (const id of ids) {
        const current = next[id] ?? 0
        const updated = current > 0 ? current - 1 : 0
        if (updated === current) {
          continue
        }
        next[id] = updated
        changed = true
      }
      if (!changed) {
        return state
      }
      return { ...state, myCategoryCounts: next }
    }),
  optimisticAddMyId: (itemId) =>
    set((state) => {
      if (!Number.isFinite(itemId) || itemId <= 0) {
        return state
      }
      if (state.myIds.has(itemId)) {
        return state
      }
      const nextIds = new Set(state.myIds)
      nextIds.add(itemId)
      return {
        ...state,
        myIds: nextIds,
        myCount: state.myCount + 1,
      }
    }),
  setPendingMyUndo: (snap) =>
    set((state) => {
      if (state.pendingMyUndo === snap) {
        return state
      }

      return {
        ...state,
        pendingMyUndo: snap ?? null,
      }
    }),
  applyMyRemovalOptimistic: (itemId) => {
    const state = get()
    const previousOrder = [...state.myAllOrderedIds]
    const existingIndex = state.myItems.findIndex((item) => item.id === itemId)
    const fallbackIndex = previousOrder.indexOf(itemId)
    const resolvedIndex = existingIndex >= 0 ? existingIndex : fallbackIndex >= 0 ? fallbackIndex : previousOrder.length
    const removedItem = existingIndex >= 0 ? state.myItems[existingIndex] : undefined

    set((current) => {
      const orderIndex = current.myAllOrderedIds.indexOf(itemId)
      const nextAll =
        orderIndex === -1
          ? current.myAllOrderedIds
          : [
              ...current.myAllOrderedIds.slice(0, orderIndex),
              ...current.myAllOrderedIds.slice(orderIndex + 1),
            ]

      const nextIds = new Set(current.myIds)
      const hadId = nextIds.delete(itemId)

      const itemMap = new Map(current.myItems.map((entry) => [entry.id, entry]))
      const removedFromItems = itemMap.delete(itemId)

      if (orderIndex === -1 && !hadId && !removedFromItems) {
        if (current.selectedPrepId === null) {
          return current
        }

        return {
          ...current,
          selectedPrepId: null,
        }
      }

      const partial = reconcileAfterOrderChange(current, nextAll, {
        itemMapOverride: itemMap,
      })

      return {
        ...current,
        ...partial,
        myIds: nextIds,
        selectedPrepId: null,
      }
    })

    return { removedItem, previousIndex: resolvedIndex, previousOrder }
  },
  applyMyUndoRestore: (snap) =>
    set((state) => {
      if (!snap?.item) {
        return state
      }

      const normalized = normalizeIdList(snap.previousOrder ?? [])
      let baseOrder = [...normalized]

      if (baseOrder.includes(snap.itemId)) {
        const currentIndex = baseOrder.indexOf(snap.itemId)
        const targetIndex = Math.max(0, Math.min(baseOrder.length - 1, snap.previousIndex))
        const moved = moveItem(baseOrder, currentIndex, targetIndex)
        if (moved) {
          baseOrder = moved
        }
      } else {
        const insertAt = Math.max(0, Math.min(baseOrder.length, snap.previousIndex))
        baseOrder.splice(insertAt, 0, snap.itemId)
      }

      const ensuredAll = ensureIdsContainSubset(state.myAllOrderedIds, baseOrder)
      const nextAll = applySubsetOrdering(ensuredAll, baseOrder)
      const itemMap = new Map(state.myItems.map((entry) => [entry.id, entry]))
      itemMap.set(snap.itemId, { ...snap.item, is_in_my_prep: true })

      const partial = reconcileAfterOrderChange(state, nextAll, {
        itemMapOverride: itemMap,
      })

      return {
        ...state,
        ...partial,
        myIds: new Set(nextAll),
      }
    }),
    setCategories: (categories) =>
      set((state) => {
        const map: Record<number, { id: number; slug: string; name: string }> = {}

        if (Array.isArray(categories)) {
          for (const entry of categories) {
            const rawId = entry?.id
            if (typeof rawId !== 'number' || !Number.isFinite(rawId)) {
              continue
            }
            const id = Math.floor(rawId)
            if (id <= 0) {
              continue
            }
            const slug = typeof entry?.slug === 'string' ? entry.slug.trim() : ''
            const name = typeof entry?.name === 'string' ? entry.name.trim() : ''
            map[id] = { id, slug, name }
          }
        }

        const sameSize = Object.keys(map).length === Object.keys(state.categoriesById).length
        const sameEntries = sameSize
          ? Object.entries(map).every(([id, value]) => {
              const existing = state.categoriesById[Number(id)]
              return existing?.name === value.name && existing?.slug === value.slug
            })
          : false

        if (sameEntries) {
          if (state.categoriesLoadedAt) {
            return state
          }
          return { ...state, categoriesLoadedAt: Date.now() }
        }

        return {
          ...state,
          categoriesById: map,
          categoriesLoadedAt: Date.now(),
        }
      }),
  }
})

export const selectMyReordering = (state: PrepListState): boolean => state.myReordering
export const selectMyOnly = (state: PrepListState): boolean => state.myOnly
export const selectMyCount = (state: PrepListState): number => state.myCount
export const selectMyItemsLoading = (state: PrepListState): boolean => state.myItemsLoading

export default usePrepListStore

const normalizeIdList = (source: unknown): number[] => {
  if (!Array.isArray(source)) {
    return []
  }

  const normalized: number[] = []
  const seen = new Set<number>()

  for (const value of source) {
    const numeric = typeof value === 'number' ? value : Number(value)
    if (!Number.isFinite(numeric)) {
      continue
    }
    const id = Math.floor(numeric)
    if (id <= 0 || seen.has(id)) {
      continue
    }
    seen.add(id)
    normalized.push(id)
  }

  return normalized
}

const ensureIdsContainSubset = (base: readonly number[], subset: readonly number[]): number[] => {
  const result: number[] = []
  const seen = new Set<number>()

  for (const value of base ?? []) {
    const numeric = typeof value === 'number' ? value : Number(value)
    if (!Number.isFinite(numeric)) {
      continue
    }
    const id = Math.floor(numeric)
    if (id <= 0 || seen.has(id)) {
      continue
    }
    seen.add(id)
    result.push(id)
  }

  for (const value of subset ?? []) {
    const numeric = typeof value === 'number' ? value : Number(value)
    if (!Number.isFinite(numeric)) {
      continue
    }
    const id = Math.floor(numeric)
    if (id <= 0 || seen.has(id)) {
      continue
    }
    seen.add(id)
    result.push(id)
  }

  return result
}

const applySubsetOrdering = (all: readonly number[], subset: readonly number[]): number[] => {
  const subsetOrder = normalizeIdList(subset)
  if (subsetOrder.length === 0) {
    return ensureIdsContainSubset(all, [])
  }

  const result: number[] = []
  const seen = new Set<number>()

  for (const id of subsetOrder) {
    if (!seen.has(id)) {
      seen.add(id)
      result.push(id)
    }
  }

  for (const value of all ?? []) {
    const numeric = typeof value === 'number' ? value : Number(value)
    if (!Number.isFinite(numeric)) {
      continue
    }
    const id = Math.floor(numeric)
    if (id <= 0 || seen.has(id)) {
      continue
    }
    seen.add(id)
    result.push(id)
  }

  return result
}

const buildLoadedOrder = (
  all: readonly number[],
  itemMap: ReadonlyMap<number, PrepListItem>,
  page: number,
  perPage: number,
): number[] => {
  if (!Array.isArray(all) || all.length === 0 || itemMap.size === 0) {
    return []
  }

  const normalizedPage = Number.isFinite(page) ? Math.max(1, Math.floor(page)) : 1
  const normalizedPerPage = clampMyPerPage(perPage)
  const loadedCount = Math.max(0, normalizedPage * normalizedPerPage)
  const sliceEnd = Math.min(all.length, loadedCount)
  const visibleSlice = all.slice(0, sliceEnd)
  const result: number[] = []
  const included = new Set<number>()

  for (const id of visibleSlice) {
    if (itemMap.has(id) && !included.has(id)) {
      included.add(id)
      result.push(id)
    }
  }

  if (included.size < itemMap.size) {
    for (const id of all) {
      if (included.has(id) || !itemMap.has(id)) {
        continue
      }
      included.add(id)
      result.push(id)
    }
  }

  return result
}

const rebuildItemsFromOrder = (
  order: readonly number[],
  itemMap: ReadonlyMap<number, PrepListItem>,
): PrepListItem[] => {
  if (itemMap.size === 0) {
    return []
  }

  const items = Array.from(itemMap.values())
  if (!order || order.length === 0) {
    return items
  }

  return orderItemsByIds(items, order)
}

const reconcileAfterOrderChange = (
  state: PrepListState,
  nextAll: readonly number[],
  options: {
    nextCount?: number
    itemMapOverride?: ReadonlyMap<number, PrepListItem>
  } = {},
): Pick<PrepListState, 'myAllOrderedIds' | 'myOrderedIds' | 'myItems' | 'myIds'> &
  Partial<Pick<PrepListState, 'myCount'>> => {
  const canonicalAll = normalizeIdList(nextAll)
  const itemMap = options.itemMapOverride ?? new Map(state.myItems.map((entry) => [entry.id, entry]))
  const loadedOrder = buildLoadedOrder(canonicalAll, itemMap, state.myPage, state.myPerPage)
  const orderedItems = rebuildItemsFromOrder(loadedOrder, itemMap)
  const nextState: Pick<PrepListState, 'myAllOrderedIds' | 'myOrderedIds' | 'myItems' | 'myIds'> &
    Partial<Pick<PrepListState, 'myCount'>> = {
    myAllOrderedIds: canonicalAll,
    myOrderedIds: loadedOrder,
    myItems: orderedItems,
    myIds: new Set(canonicalAll),
  }

  if (typeof options.nextCount === 'number' && Number.isFinite(options.nextCount)) {
    nextState.myCount = Math.max(0, Math.floor(options.nextCount))
  }

  return nextState
}

const collectCategoryIds = (item: PrepListItem): number[] => {
  if (!item) {
    return []
  }

  const ids = new Set<number>()

  const primaryId =
    typeof item.primary_category_id === 'number' && Number.isFinite(item.primary_category_id)
      ? Math.floor(item.primary_category_id)
      : item.primary_category?.id

  if (typeof primaryId === 'number' && Number.isFinite(primaryId) && primaryId > 0) {
    ids.add(primaryId)
  }

  if (Array.isArray(item.categories)) {
    for (const category of item.categories) {
      const id = category?.id
      if (typeof id === 'number' && Number.isFinite(id) && id > 0) {
        ids.add(id)
      }
    }
  }

  return Array.from(ids)
}

const normalizeMyPrepItems = (
  items: unknown,
): { items: PrepListItem[]; orderedIds: number[] } => {
  if (!Array.isArray(items)) {
    return { items: [], orderedIds: [] }
  }

  const normalized: PrepListItem[] = []

  for (const entry of items) {
    const sanitized = sanitizePrepListItem(entry, { inMyPrep: true })
    if (sanitized) {
      normalized.push(sanitized)
    }
  }

  return { items: normalized, orderedIds: normalized.map((item) => item.id) }
}


const areImagesEqual = (
  a: PrepListItem['image'],
  b: PrepListItem['image'],
): boolean => {
  if (!a && !b) {
    return true
  }

  if (!a || !b) {
    return false
  }

  const normalizeString = (value: string | undefined): string => (typeof value === 'string' ? value : '')

  return (
    (a.media_id ?? null) === (b.media_id ?? null) &&
    normalizeString(a.thumb) === normalizeString(b.thumb) &&
    normalizeString(a.alt) === normalizeString(b.alt)
  )
}

const orderItemsByIds = (
  items: readonly PrepListItem[],
  orderedIds: readonly number[],
): PrepListItem[] => {
  if (!Array.isArray(items) || items.length === 0) {
    return []
  }

  if (!Array.isArray(orderedIds) || orderedIds.length === 0) {
    return [...items]
  }

  const lookup = new Map(items.map((entry) => [entry.id, entry]))
  const ordered: PrepListItem[] = []

  for (const id of orderedIds) {
    const match = lookup.get(id)
    if (match) {
      ordered.push(match)
      lookup.delete(id)
    }
  }

  if (lookup.size > 0) {
    ordered.push(...lookup.values())
  }

  return ordered
}

const moveItem = <T>(source: readonly T[], fromIndex: number, toIndex: number): T[] | null => {
  if (!Array.isArray(source) || source.length === 0) {
    return null
  }

  const safeFrom = Math.max(0, Math.min(source.length - 1, fromIndex))
  const safeTo = Math.max(0, Math.min(source.length - 1, toIndex))

  if (safeFrom === safeTo) {
    return null
  }

  const next = [...source]
  const [moved] = next.splice(safeFrom, 1)

  if (moved === undefined) {
    return null
  }

  next.splice(safeTo, 0, moved)
  return next
}
